import CloseRounded from '@mui/icons-material/CloseRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, unwrap, type Schemas } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable, SearchField } from '../../../components/DataTable';
import { EmptyState, PageHeader } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { dateTime, initials, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { DateRangeControl } from '../components/DateRangeControl';
import { Field, NoAccess, QueryError } from '../components/ui';
import { linkTo } from '../components/useEntityLink';
import { useReportParams } from '../components/useReportParams';
import { actionLabel, describeChanges, fieldLabel } from '../lib/auditChanges';
import { downloadCsv, reportFileName } from '../lib/csv';
import { entityLabel } from '../lib/entityLinks';

type AuditEntry = Schemas['AuditEntry'];

const AUDIT_MODULES = [
  'auth',
  'sales',
  'purchases',
  'payments',
  'returns',
  'quotations',
  'inventory',
  'catalog',
  'contacts',
  'accounting',
  'reminders',
  'staff',
  'settings',
  'subscription',
  'sync',
];

const OUTCOMES = ['SUCCESS', 'FAILURE', 'DENIED'] as const;
type Outcome = (typeof OUTCOMES)[number];

const outcomeChip = (o: Outcome) => (
  <StatusChip
    status={o === 'SUCCESS' ? 'COMPLETED' : o === 'DENIED' ? 'PARTIAL' : 'VOID'}
    label={o === 'SUCCESS' ? 'Success' : o === 'DENIED' ? 'Denied' : 'Failed'}
  />
);

const APPS: Record<string, string> = {
  api: 'Web',
  web: 'Web',
  desktop: 'Desktop',
  admin: 'Admin panel',
};
const appLabel = (app: string) => APPS[app.toLowerCase()] ?? fieldLabel(app);

function actorName(e: AuditEntry): string {
  if (e.actorName) return e.actorName;
  if (e.actorType === 'SYSTEM') return 'System';
  if (e.actorType === 'DEVICE') return 'Desktop device';
  if (e.actorType === 'PLATFORM_ADMIN') return 'Platform support';
  return 'Someone';
}

export function AuditPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const p = useReportParams('7d');
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [exporting, setExporting] = useState(false);
  const module = p.get('module') ?? undefined;
  const outcome = OUTCOMES.find((o) => o === p.get('outcome'));
  const action = p.get('action') ?? '';
  const filters = { ...p.range, module, outcome, action: action || undefined };
  const query = { ...filters, page: p.page, pageSize: p.pageSize };
  const allowed = can('audit.view');
  const q = useQuery({
    queryKey: ['audit-logs', query],
    queryFn: () => unwrap(api.GET('/audit-logs', { params: { query } })),
    placeholderData: keepPreviousData,
    enabled: allowed,
  });
  const rows = q.data && typeof q.data !== 'string' ? q.data : undefined;

  return (
    <>
      <PageHeader
        title="Activity log"
        subtitle="Who did what, and when — every change, sign-in and denied attempt in your shop."
        actions={
          allowed && (
            <Tooltip
              title={
                can('reports.export')
                  ? 'Download the filtered log as CSV'
                  : 'Needs export permission'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FileDownloadOutlined />}
                  disabled={!can('reports.export')}
                  loading={exporting}
                  sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
                  onClick={() => {
                    setExporting(true);
                    downloadCsv(
                      api.GET('/audit-logs', {
                        params: { query: { ...filters, format: 'csv' } },
                        parseAs: 'text',
                      }),
                      reportFileName('activity-log', p.range),
                    )
                      .then(() => notify.success('CSV downloaded'))
                      .catch((e: unknown) => notify.error(e))
                      .finally(() => setExporting(false));
                  }}
                >
                  Export CSV
                </Button>
              </span>
            </Tooltip>
          )
        }
      />
      {!allowed ? (
        <NoAccess what="the activity log" />
      ) : (
        <>
          {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
          <DataTable
            loading={q.isLoading}
            rows={rows?.data}
            rowKey={(e) => e.id}
            page={rows?.page}
            onPageChange={p.setPage}
            onPageSizeChange={p.setPageSize}
            onRowClick={setSelected}
            toolbar={
              <Stack
                direction="row"
                sx={{ gap: 1.5, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}
              >
                <DateRangeControl
                  range={p.range}
                  preset={p.preset}
                  onPreset={p.setPreset}
                  onCustom={p.setCustom}
                />
                <TextField
                  select
                  size="small"
                  fullWidth={false}
                  label="Area"
                  value={module ?? ''}
                  onChange={(e) => p.update({ module: e.target.value || null })}
                  sx={{ minWidth: 150 }}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">All areas</MenuItem>
                  {AUDIT_MODULES.map((m) => (
                    <MenuItem key={m} value={m}>
                      {fieldLabel(m)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  fullWidth={false}
                  label="Outcome"
                  value={outcome ?? ''}
                  onChange={(e) => p.update({ outcome: e.target.value || null })}
                  sx={{ minWidth: 140 }}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">Any outcome</MenuItem>
                  <MenuItem value="SUCCESS">Success</MenuItem>
                  <MenuItem value="FAILURE">Failed</MenuItem>
                  <MenuItem value="DENIED">Denied</MenuItem>
                </TextField>
                <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                  <SearchField
                    value={action}
                    onChange={(v) => p.update({ action: v.trim() || null })}
                    placeholder="Action, e.g. sales_invoice.post"
                  />
                </Box>
              </Stack>
            }
            empty={
              <EmptyState
                icon={<HistoryRounded />}
                title="No activity found"
                body="Nothing matches these filters. Try a longer date range or clear a filter."
              />
            }
            columns={[
              {
                key: 'when',
                header: 'When',
                width: 150,
                hideBelow: 'sm',
                render: (e) => (
                  <Tooltip title={dateTime(e.occurredAt, format)}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      {relativeTime(e.occurredAt)}
                    </Typography>
                  </Tooltip>
                ),
              },
              {
                key: 'who',
                header: 'Who',
                hideBelow: 'sm',
                render: (e) => (
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                      }}
                    >
                      {initials(actorName(e))}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {actorName(e)}
                    </Typography>
                  </Stack>
                ),
              },
              {
                key: 'what',
                header: 'What',
                render: (e) => (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {actionLabel(e.action)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ display: { sm: 'none' } }}
                    >
                      {actorName(e)} · {relativeTime(e.occurredAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fieldLabel(e.module)}
                      {e.entityType ? ` · ${entityLabel(e.entityType)}` : ''}
                      {e.failureReason ? ` · ${e.failureReason}` : ''}
                    </Typography>
                  </Box>
                ),
              },
              { key: 'app', header: 'App', hideBelow: 'lg', render: (e) => appLabel(e.app) },
              {
                key: 'outcome',
                header: 'Outcome',
                align: 'right',
                render: (e) => outcomeChip(e.outcome),
              },
            ]}
          />
        </>
      )}
      <AuditDrawer entry={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function AuditDrawer({ entry, onClose }: { entry: AuditEntry | null; onClose: () => void }) {
  const { format } = useShop();
  const view = describeChanges(entry?.changes);
  const to = entry ? linkTo(entry.entityType, entry.entityId) : null;
  return (
    <Drawer
      anchor="right"
      open={Boolean(entry)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, p: 3 } } }}
    >
      {entry && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}
          >
            <Box>
              <Typography variant="h5">{actionLabel(entry.action)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {dateTime(entry.occurredAt, format)} · {relativeTime(entry.occurredAt)}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
            <Field label="Who">{actorName(entry)}</Field>
            <Field label="Outcome">{outcomeChip(entry.outcome)}</Field>
            <Field label="Area">{fieldLabel(entry.module)}</Field>
            <Field label="App">{appLabel(entry.app)}</Field>
            {entry.entityType && <Field label="Record">{entityLabel(entry.entityType)}</Field>}
            {entry.ip && <Field label="IP address">{entry.ip}</Field>}
          </Box>
          {entry.failureReason && (
            <Box sx={{ mb: 2.5 }}>
              <Field label="Reason">{entry.failureReason}</Field>
            </Box>
          )}
          {to && (
            <Button
              component={RouterLink}
              to={to}
              variant="outlined"
              endIcon={<OpenInNewRounded />}
              sx={{ mb: 2.5, alignSelf: 'flex-start' }}
            >
              Open {entityLabel(entry.entityType).toLowerCase()}
            </Button>
          )}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Changes
          </Typography>
          {view.kind === 'none' && (
            <Typography variant="body2" color="text.secondary">
              No field changes were recorded for this action.
            </Typography>
          )}
          {view.kind === 'values' && (
            <Table size="small">
              <TableBody>
                {view.rows.map((r) => (
                  <TableRow key={r.field}>
                    <TableCell sx={{ color: 'text.secondary', width: '40%' }}>{r.field}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {view.kind === 'diff' && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Before</TableCell>
                  <TableCell>After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.rows.map((r) => (
                  <TableRow key={r.field}>
                    <TableCell sx={{ color: 'text.secondary' }}>{r.field}</TableCell>
                    <TableCell
                      sx={{
                        wordBreak: 'break-word',
                        color: r.changed ? 'error.main' : 'text.secondary',
                        textDecoration: r.changed && r.before !== '—' ? 'line-through' : undefined,
                      }}
                    >
                      {r.before}
                    </TableCell>
                    <TableCell
                      sx={{
                        wordBreak: 'break-word',
                        color: r.changed ? 'success.main' : undefined,
                        fontWeight: r.changed ? 600 : undefined,
                      }}
                    >
                      {r.after}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {entry.requestId && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 3, display: 'block' }}>
              Request {entry.requestId}
            </Typography>
          )}
        </>
      )}
    </Drawer>
  );
}
